import React, { useState, useEffect, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { downloadDocument } from '../../Helpers/applicantHelper';
import AssignedUserModal from '../Modals/assignedUserModal';
import CommentsModal from '../Modals/commentModal';
import { addOrUpdateCommentToApplication, sendAutomatedEmail } from '../../Services/applicationService';
import CalendarEventModal from '../Modals/calendarEventModal';
import { createCalendarEvent } from '../../Services/calendarService';
import SendEmailModal from '../Modals/sendEmailModal';
import { CalendarEventAttendeeType, CreatedBy } from '../../Helpers/enums';
import moment from 'moment/min/moment-with-locales';
import { calculatePercentage, getRadialBarClass } from '../../Services/utils';
import { AssignedUserType } from '../Modals/assignedUserModal';
import { createdByIcon } from '../../Helpers/applicantHelper';
import QuestionnaireModal from '../Modals/questionnaireModal';
import useAuthStore from '../../../../stores/authStore';
import UserTopRepositories from '../Modals/UserTopRepositories';

const ApplicationCard = ({ item, index, refreshStatus, parsedKanbanSettings, column, setIsDroppedToFalse }) => {
  const globalUser = useAuthStore((state) => state.user);
  const { t } = useTranslation();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] = useState(false);
  const [application, setApplication] = useState();
  const [calendarEvent, setCalendarEvent] = useState({});
  const [isCalendarEventModalOpen, setIsCalendarEventModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [showCountdownLayer, setShowCountdownLayer] = useState(false);
  const [countdownValue, setCountdownValue] = useState(null);
  const countdownTimeoutRef = useRef(null);

  useEffect(() => {
    if (column.sendAutomatedEmail && item.isDropped) {
      startCountdown();

      return () => {
        //clearInterval(countdownTimeoutRef.current);
      };
    }
  }, [column.sendAutomatedEmail, item.isDropped]);

  const handleCountdownLayerClick = () => {
    if (!isDragging && countdownValue > -3) {
      setCountdownValue(-10);
      setShowCountdownLayer(false);
      clearInterval(countdownTimeoutRef.current);
    }
  };

  const handleMouseDown = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    if (!isDragging) {
      handleCountdownLayerClick();
    }
  };

  const handleMouseMove = () => {
    setIsDragging(true);
  };

  const startCountdown = () => {
    setCountdownValue(10);
    setIsDroppedToFalse(item.applicationId);
    setShowCountdownLayer(true);
    countdownTimeoutRef.current = setInterval(() => {
      setCountdownValue((prevValue) => {
        if (prevValue === 0) {
          sendEmail();
        }
        if (prevValue <= -3) {
          clearInterval(countdownTimeoutRef.current);
          setShowCountdownLayer(false);
          return prevValue;
        }
        return prevValue - 1;
      });
    }, 1000);
  };

  const sendEmail = () => {
    sendAutomatedEmail(item.applicationId, 0, '', 0, '', true, column.emailSubject, column.emailContent);
  };

  const toggleAssignedUserModal = (application) => {
    setIsUserModalOpen(!isUserModalOpen);
    setApplication((prev) => application);
  };

  const toggleCommentsModal = (item) => {
    setApplication((prev) => item);
    setIsCommentsModalOpen((prev) => !prev);
  };

  const toggleQuestionnaireModal = (fullScorePercentage) => {
    setIsQuestionnaireModalOpen((prev) => !prev);

    if (fullScorePercentage === undefined || typeof fullScorePercentage !== 'number') {
      return;
    }

    if (fullScorePercentage) {
      setApplication((prev) => ({
        ...item,
        fullScorePercentage: fullScorePercentage,
      }));
    }
  };

  const handleValidCommentSubmit = (values) => {
    const commentForCreate = {
      commentTitle: values.commentTitle,
      applicationId: application.applicationId,
      commentMessage: values.messageTextArea,
      rating: values.commentRating,
    };

    addOrUpdateCommentToApplication(commentForCreate).then((response) => {
      toggleCommentsModal();
      refreshStatus();
    });
  };

  const toggleCalendarEventModal = () => {
    setIsCalendarEventModalOpen(!isCalendarEventModalOpen);
    setCalendarEvent({});
  };

  const handleValidCalendarEventSubmit = (values) => {
    createEvent(
      values.calendarEventTimeStart,
      values.calendarEventTimeEnd,
      values.calendarEventTitle,
      values.notesTextArea,
      values.asyncSelectAttendees
    );
    toggleCalendarEventModal();
  };

  const createEvent = async (start, end, title, description, attendees) => {
    let { data: newEvent } = await createCalendarEvent({ start, end, title, description, attendees });
  };

  const toggleEmailModal = () => {
    setApplication(item);
    setIsEmailModalOpen((prev) => !prev);
  };

  let startDateTime = new Date();
  startDateTime.setDate(startDateTime.getDate() + 1);
  startDateTime.setHours(12, 0, 0, 0);
  let endDateTime = new Date(startDateTime.getTime() + 30 * 60 * 1000);

  const getAllModals = () => {
    return (
      <>
        {isCalendarEventModalOpen && (
          <CalendarEventModal
            calendarEvent={{
              start: startDateTime,
              end: endDateTime,
              attendees: [
                {
                  value: item.applicantId,
                  label: item.applicantFirstName + ' ' + item.applicantLastName,
                  emailAttendeeType: CalendarEventAttendeeType.Applicant,
                },
              ],
            }}
            showCalendarEventModal={isCalendarEventModalOpen}
            onHandleValidSubmit={handleValidCalendarEventSubmit}
            onToggle={toggleCalendarEventModal}
            modalPurpose={'create'}
          />
        )}
        {isCommentsModalOpen && (
          <CommentsModal
            showModal={isCommentsModalOpen}
            editComment={true}
            onToggle={(value) => toggleCommentsModal(value, null)}
            onHandleValidSubmit={handleValidCommentSubmit}
          />
        )}
        {isQuestionnaireModalOpen && (
          <QuestionnaireModal item={item} showModal={isQuestionnaireModalOpen} onToggle={toggleQuestionnaireModal} />
        )}
        {isUserModalOpen && (
          <AssignedUserModal
            showModal={isUserModalOpen}
            onToggle={(application) => toggleAssignedUserModal(application)}
            onStatusRefresh={() => refreshStatus()}
            application={application}
            assignedUserType={AssignedUserType.AssignedToApplication}
          />
        )}
        {isEmailModalOpen && (
          <SendEmailModal
            isEmailModalOpen={isEmailModalOpen}
            toggleIsEmailModalOpen={() => setIsEmailModalOpen(!isEmailModalOpen)}
            sender={{
              id: globalUser.Id,
              name: globalUser.firstName,
              lastName: globalUser.lastName,
              email: globalUser.email,
            }}
            recipient={{
              id: application?.applicantId,
              name: application?.applicantFirstName,
              lastName: application?.applicantLastName,
              email: application?.applicantEmail,
            }}
          />
        )}
      </>
    );
  };

  const goToTimeline = () => {
    window.open(
      `/applications/details?applicationId=${item.applicationId}&tab=applicantTimeline`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const daysAgo = moment().diff(moment.utc(item.created), 'days');
  const hoursAgo = moment().diff(moment.utc(item.created), 'hours');

  const previousColumnName = (columnId) => {
    const item = parsedKanbanSettings?.find((obj) => obj.Id === columnId);
    return item ? item.Title : null;
  };

  const composeLastAction = () => {
    const nameOfRecruiter = item.lastStateChange?.nameOfRecruiter;
    const prevColumn = previousColumnName(item.lastStateChange?.previousStateId);

    if (daysAgo > 0) {
      return (
        <span>
          <b>{prevColumn}</b> {'->'} <b>{column.title}</b>{' '}
          <span className="text-muted">
            {daysAgo === 0 ? hoursAgo + 'h' : daysAgo + t('common.DAY_SHORT')} {nameOfRecruiter}
          </span>
        </span>
      );
    } else {
      return (
        <span>
          <b>{prevColumn}</b> {'->'} <b>{column.title}</b>{' '}
          <span className="text-muted">
            {daysAgo === 0 ? hoursAgo + 'h' : daysAgo + t('common.DAY_SHORT')} {nameOfRecruiter}
          </span>
        </span>
      );
    }
  };

  const showScoreGraph = item.scoreCombined && item.maxScore > 0;

  const composeCard = (
    <div
      className="card"
      draggable="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showCountdownLayer && (
        <div
          className="countdown-layer"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onClick={handleCountdownLayerClick}
        >
          {countdownValue >= 0 ? (
            <>
              <p>
                {t('common.SENDING_EMAIL_IN')} <br />
                {countdownValue} {t('common.SECONDS')}
              </p>
              <p>{t('common.CLICK_TO_CANCEL')}</p>
            </>
          ) : (
            <p>{t('applicants.EMAIL_SENT')}</p>
          )}
        </div>
      )}
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <h4 className="text-truncate p-0 mb-1">
              <a href={`/applications/details?applicationId=${item.applicationId}&tab=applicationDetails`}>
                <b>{item.applicantFirstName + ' ' + item.applicantLastName}</b>
              </a>
              &nbsp;
              <small title={t('common.CREATED')} className="text-muted">
                {daysAgo === 0 ? hoursAgo + 'h' : daysAgo + t('common.DAY_SHORT')}
              </small>
            </h4>
            <div className="col-2 m-0 p-0 d-flex justify-content-end">
              <span
                data-label={calculatePercentage(application?.fullScorePercentage || item.fullScorePercentage, 100)}
                className={getRadialBarClass(application?.fullScorePercentage || item.fullScorePercentage)}
              />
            </div>
          </div>
        </div>
        <div className="row flex-nowrap align-items-center">
          <div className="col-3">
            <div
              className="img-hover-wrapper"
              onClick={() => {
                downloadDocument(item.cv, item.applicantFirstName + '_' + item.applicantLastName + '_CV', t);
              }}
            >
              <img
                id="imgApplicantProfilePic"
                src={item.profilePicture ? item.profilePicture : 'img/user/user-dummy-300x300.png'}
                className="img-thumbnail rounded-circle"
                alt={'User profile should be here'}
                style={{ cursor: 'pointer' }}
              />
              <div className="img-hover-overlay">CV</div>
              <UncontrolledTooltip delay={{ show: 100, hide: 0 }} placement="top" target="imgApplicantProfilePic">
                {t('applicants.OPEN_CV')}
              </UncontrolledTooltip>
            </div>
          </div>
          <div className={showScoreGraph === true ? 'col-9 text-truncate p-0 m-0' : 'col-9 text-truncate p-0 m-0'}>
            <a href={`/jobs/${item.jobId}`} style={{ color: '#333', fontWeight: 600 }}>
              {item.jobTitle}
            </a>
            <div className="row">
              <div className="col" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                {createdByIcon(item.applicationId, item.createdBy, t)}
                {item.linkedInUrl && (
                  <a
                    href={item.linkedInUrl}
                    className="fa-lg mr-1 fa-brands fa-linkedin far icon-hover-effect-no-color"
                    title={t('applicant.LINKEDIN_URL')}
                    style={{ textDecoration: 'none' }}
                  />
                )}
                {item.gitHubUrl && (
                  <a
                    id="gitHubUserId"
                    href={item.gitHubUrl}
                    className="fa-lg mr-1 fa-brands fa-github far icon-hover-effect-no-color"
                    title={t('applicant.GITHUB_URL')}
                    style={{ textDecoration: 'none' }}
                  />
                )}
                {!showCountdownLayer && isHovered && item.applicantEmail && (
                  <i
                    className="fa-lg mr-1 far icon-envelope fa-fw icon-hover-effect text-primary"
                    title={t('email.NEW_EMAIL')}
                    onClick={toggleEmailModal}
                  ></i>
                )}
                {!showCountdownLayer && isHovered && (
                  <i
                    className="fa-lg mr-1 far icon-calendar fa-fw icon-hover-effect text-success"
                    title={t('calendar.NEW_MEETING')}
                    onClick={toggleCalendarEventModal}
                  ></i>
                )}
                {!showCountdownLayer && isHovered && (
                  <i
                    className="fa-lg mr-1 far icon-bubbles fa-fw icon-hover-effect text-info"
                    title={t('comments.NEW_COMMENT')}
                    onClick={() => toggleCommentsModal(item)}
                  ></i>
                )}
                {!showCountdownLayer && isHovered && (
                  <i
                    className="fa-lg mr-1 far icon-badge fa-fw icon-hover-effect text-warning"
                    title={t('applications.START_QUESTIONNAIRE')}
                    onClick={() => toggleQuestionnaireModal()}
                  ></i>
                )}
                {!showCountdownLayer && isHovered && (
                  <i
                    className="fa-lg ml-1 icon-graph far icon-hover-effect text-purple clickable"
                    title={t('kanban.SHOW_FULL_TIMELINE')}
                    onClick={() => goToTimeline()}
                  ></i>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          {item.createdBy === CreatedBy.RecruiterFromGitHub &&
            item.gitHubProfile &&
            (() => {
              let gitHubProfile = item.gitHubProfile;
              if (typeof gitHubProfile === 'string') {
                try {
                  gitHubProfile = JSON.parse(gitHubProfile);
                } catch (e) {
                  gitHubProfile = null;
                }
              }
              if (!gitHubProfile) return null;
              return <UserTopRepositories user={gitHubProfile} />;
            })()}
        </div>
        <div className="row mt-1">
          <div className="col-10 text-hover-effect text-muted clickable" onClick={() => toggleAssignedUserModal(item)}>
            <i className="fa fa-user-circle far"></i>
            <span className="ml-1">{item.assignedTo}</span>
          </div>
        </div>
        {/* {item.lastStateChange && (
          <>
            <hr style={{ margin: '5px 0' }} />
            <div className="row align-items-center" style={{ whiteSpace: 'normal' }}>
              <div className="col">{composeLastAction()}</div>
            </div>
          </>
        )} */}
      </div>
    </div>
  );

  return (
    <Draggable key={item.applicationId?.toString()} draggableId={item.applicationId?.toString()} index={index}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
          {getAllModals()}
          {composeCard}
        </div>
      )}
    </Draggable>
  );
};

export default ApplicationCard;
